import { FC, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Button, Modal, Typography } from 'antd';
import { capitalCase } from 'change-case';
import { TbUserCancel, TbUserPause } from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { memberService } from '@/services';
import { Member, MemberStatusType } from '@/types';

interface DangerModalProps {
    member?: Member;
    onUpdated: (member: Member) => void;
    open: boolean;
    setOpen: (b: boolean) => void;
    value: 'block' | 'deactivate';
}

const DangerModal: FC<DangerModalProps> = ({ member, onUpdated, open, setOpen, value }): JSX.Element => {
    const [loading, setLoading] = useState(false);

    const handleConfirmation = async () => {
        if (!member?.id) {return;}

        try {
            setLoading(true);
            const status = value === 'block' ? MemberStatusType.BLOCKED : MemberStatusType.DEACTIVATED;
            const updatedMember = await memberService.update(member.id, { status });

            onUpdated(updatedMember);
            setOpen(false);
            toast(value === 'block' ? 'Member Blocked' : 'Member Deactivated', {
                icon: value === 'block' ? <TbUserCancel size='1rem' /> : <TbUserPause size='1rem' />,
                description: `${member.user?.firstName} ${member.user?.lastName}`,
            });
        } catch (error) {
            captureException(error);
            toast.error(value === 'block' ? 'Unable to Block Member' : 'Unable to Deactivate Member');
        } finally {
            setLoading(false);
        }
    };

    const { token } = useAntDesignTheme();

    return (
        <Modal
            title={`${capitalCase(value as string)} User`}
            open={open}
            onCancel={() => setOpen(false)}
            style={{ minWidth: '37.5vw' }}
            footer={[
                <Button key='cancel' onClick={() => setOpen(false)}>
                    Cancel
                </Button>,
                <Button key='confirm' danger loading={loading} type='primary' onClick={handleConfirmation}>
                    {capitalCase(value)} User
                </Button>,
            ]}
        >
            <div style={{ textAlign: 'center' }}>
                <Avatar src={member?.user?.avatar} />
                <Typography.Title level={5} style={{ marginTop: '1rem' }}>
                    {member?.user?.firstName} {member?.user?.lastName}
                </Typography.Title>
                <span style={{ color: token.colorTextTertiary }}>{member?.user?.emailAddress}</span>
            </div>
        </Modal>
    );
};

export default DangerModal;
